import React, {ReactElement} from 'react';
import {Formik} from 'formik';
import {useIntl} from 'react-intl';
import {cx} from '@emotion/css';
import SinglePageForm from '../../DesignSystem/SinglePageForm/SinglePageForm';
import {CalculatorParams} from '../../../data/useBusinessLoanCalculator/useBusinessLoanCalculator';
import NumberWithFormikField from '../../DesignSystem/Fields/NumberField/NumberWithFormikField';
import CurrencyWithFormikField from '../../DesignSystem/Fields/Currency/CurrencyWithFormikField';
import RadioIconFieldWithFormikField from '../../DesignSystem/Fields/RadioIconField/RadioIconFieldWithFormikField';
import styles from './CalculatorForm.styles';
import {ButtonInfo} from '../../DesignSystem/Fields/RadioIconField/RadioIconField';

export interface CalculatorFormValues {
  interestRate?: number;
  loanAmount?: number;
  termLength: string;
}

interface Props {
  submitCalculator: ({interestRate, loanAmount, termLength}: CalculatorParams) => void;
  clearCalculator: () => void;
  initialValues?: CalculatorFormValues;
}

function CalculatorForm({submitCalculator, clearCalculator, initialValues = {termLength: '48'}}: Props): ReactElement {
  const {formatMessage} = useIntl();

  const generateTermLengthOptions = (): ButtonInfo[] => {
    const options: ButtonInfo[] = [];

    for (let index = 12; index <= 72; index += 12) {
      const indexString = index.toString();

      options.push({
        text: formatMessage(
          {id: 'phoenix.fields.businessLoanCalculator.termLength.option.label'},
          {value: indexString}
        ),
        value: indexString,
        key: `termsLengthOptions_${indexString}`,
      });
    }

    return options;
  };

  const convertValuesToNumbers = ({interestRate, loanAmount, termLength}: CalculatorFormValues): CalculatorParams => ({
    interestRate: interestRate || 0,
    loanAmount: loanAmount || 0,
    termLength: parseInt(termLength, 10),
  });

  const isValueFilled = (value?: number): boolean => {
    if (typeof value !== 'undefined' && value.toString().length && value !== 0) {
      return true;
    }

    return false;
  };

  // value has to be string | number since different inputs return different types
  const handleOnChange = (name: string, value: string | number, values: CalculatorFormValues): void => {
    const updatedValues = {
      ...values,
      [name]: value,
    };

    // It checks the length of the input value cause every field needs to be filled out for it to submit
    if (
      isValueFilled(updatedValues.interestRate) &&
      isValueFilled(updatedValues.loanAmount) &&
      updatedValues.termLength.length
    ) {
      const numberValues = convertValuesToNumbers(updatedValues);
      submitCalculator(numberValues);
    } else {
      clearCalculator();
    }
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={() => {
        // no-op
      }}
      validateOnChange={false}
      validateOnBlur={false}
    >
      {({errors, handleSubmit, setFieldValue, values}) => (
        <SinglePageForm
          errors={errors}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
          className={cx(styles.calculatorForm)}
        >
          <CurrencyWithFormikField<CalculatorFormValues>
            label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.loanAmount.label'})}
            placeholder={formatMessage({id: 'phoenix.fields.businessLoanCalculator.loanAmount.placeholder'})}
            name="loanAmount"
            required
            onChange={(name, value) => {
              setFieldValue(name, value);
              handleOnChange(name, value, values);
            }}
            maxLength={8}
          />
          <NumberWithFormikField<CalculatorFormValues>
            after="%"
            label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.interestRate.label'})}
            placeholder={formatMessage({id: 'phoenix.fields.businessLoanCalculator.interestRate.placeholder'})}
            name="interestRate"
            required
            onChange={(name, value) => {
              setFieldValue(name, value);
              handleOnChange(name, value, values);
            }}
            maxLength={6}
            errorMessages={{required: formatMessage({id: 'phoenix.errors.numberOfShareholders.empty'})}}
          />
          <RadioIconFieldWithFormikField<CalculatorFormValues>
            label={formatMessage({id: 'phoenix.fields.businessLoanCalculator.termLength.label'})}
            name="termLength"
            required
            onChange={(name, value) => {
              setFieldValue(name, value);
              handleOnChange(name, value, values);
            }}
            optionClassname={cx(styles.radioField)}
            options={generateTermLengthOptions()}
          />
        </SinglePageForm>
      )}
    </Formik>
  );
}

export default CalculatorForm;
